export const TDS_SECTION_CUTOVER_DATE = "2026-03-31";

/**
 * Invoices/bills dated on or before 2026-03-31 use expired TDS sections.
 */
export function shouldUseExpiredTdsRates(documentDate: string): boolean {
  if (!documentDate) {
    return true;
  }
  return documentDate.slice(0, 10) <= TDS_SECTION_CUTOVER_DATE;
}

export function formatTdsRateLabel(
  name: string,
  percentage: number,
  isExpired: boolean,
): string {
  const base = `${name} (${percentage}%)`;
  return isExpired ? `${base} (Expired)` : base;
}

/**
 * Short serial for a TDS section, for disambiguating same-name/rate rows in a
 * dropdown. Strips the "Section 393(n)" prefix from new-era codes and leaves old
 * codes as-is.
 *   "Section 393(1) Sl6(iii)D(b)-Fees" -> "Sl6(iii)D(b)-Fees"
 *   "194J"                             -> "194J"
 */
export function shortTdsSection(section: string | undefined | null): string {
  if (!section) {
    return "";
  }
  return section.replace(/^Section\s*393\(\d+\)\s*/i, "").trim();
}

export function sortTdsRatesForPartyForm<
  T extends { tds_tax_name: string; is_expired?: boolean },
>(rates: T[]): T[] {
  const active: T[] = [];
  const expired: T[] = [];

  for (const rate of rates) {
    if (rate.is_expired) {
      expired.push(rate);
    } else {
      active.push(rate);
    }
  }

  const byName = (a: T, b: T) =>
    a.tds_tax_name.localeCompare(b.tds_tax_name);

  active.sort(byName);
  expired.sort(byName);

  return [...active, ...expired];
}

export function filterTdsRatesForDocumentDate<
  T extends { is_expired?: boolean },
>(rates: T[], documentDate: string): T[] {
  const useExpired = shouldUseExpiredTdsRates(documentDate);
  return rates.filter((rate) =>
    useExpired ? !!rate.is_expired : !rate.is_expired,
  );
}

export function isTdsTaxIdValidForDocumentDate(
  tdsTaxId: string | undefined | null,
  tdsRates: Array<{ tds_tax_id: string; is_expired?: boolean }>,
  documentDate: string,
): boolean {
  if (!tdsTaxId) {
    return true;
  }

  const rate = tdsRates.find((r) => r.tds_tax_id === tdsTaxId);
  if (!rate) {
    return false;
  }

  const useExpired = shouldUseExpiredTdsRates(documentDate);
  const isExpired = rate.is_expired ?? true;
  return useExpired ? isExpired : !isExpired;
}

export function applyDocumentDateTdsToLineItems<
  T extends {
    tds_tax_id?: string | null;
    tds_tax_name?: string | null;
    tds_tax_percentage?: number | null | undefined;
  },
>(
  lineItems: T[],
  tdsRates: Array<{ tds_tax_id: string; is_expired?: boolean }>,
  documentDate: string,
): T[] {
  return lineItems.map((item) => {
    if (
      isTdsTaxIdValidForDocumentDate(item.tds_tax_id, tdsRates, documentDate)
    ) {
      return item;
    }

    return {
      ...item,
      tds_tax_id: "",
      tds_tax_name: "",
      tds_tax_percentage: undefined,
    };
  });
}

export function lineItemsTdsChanged<T extends { tds_tax_id?: string | null }>(
  before: T[],
  after: T[],
): boolean {
  if (before.length !== after.length) {
    return true;
  }

  return before.some(
    (item, index) => item.tds_tax_id !== after[index]?.tds_tax_id,
  );
}
